import { Dialog, useDialogStore } from '@ariakit/react';
import { addCategoryToEmployer } from '@ben/columbia/api';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import Input from '@ben/orion/input';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ChangeEvent, ForwardedRef } from 'react';
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';

interface IAddCategoryModal {
  employer: number;
}

const CATEGORY_NAME_REQUIRED = 'Category name is required';

const AddCategoryModal = forwardRef(
  ({ employer }: IAddCategoryModal, ref: ForwardedRef<unknown>) => {
    const dialog = useDialogStore();
    const queryClient = useQueryClient();
    const [categoryName, setCategoryName] = useState<string>('');
    const [rank, setRank] = useState<number | undefined>();
    const [categoryNameError, setCategoryNameError] = useState<
      string | undefined
    >();

    useImperativeHandle(ref, () => {
      return {
        showModal: (initialCategoryName: string) => {
          setCategoryName(initialCategoryName);
          dialog.show();
        },
        hideModal: () => {
          dialog.hide();
        },
      };
    }, [dialog]);

    const handleChangeCategoryName = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setCategoryName(value);
        if (categoryNameError) {
          setCategoryNameError(value ? undefined : CATEGORY_NAME_REQUIRED);
        }
      },
      [categoryNameError]
    );

    const handleClose = useCallback(() => {
      setRank(undefined);
      setCategoryName('');
      setCategoryNameError(undefined);
      dialog.hide();
    }, [dialog]);

    const addCategory = useMutation(
      addCategoryToEmployer(employer, {
        name: categoryName,
        rank: rank as number,
      }),
      {
        onSuccess: () => {
          void queryClient.invalidateQueries([
            `categories-for-employer-${employer}`,
          ]);
          toast.success('Category created successfully');
          handleClose();
        },
        onError: () => {
          toast.error('Error creating category');
        },
      }
    );

    const handleCreateCategory = useCallback(() => {
      if (!categoryName && !rank) {
        setCategoryNameError(CATEGORY_NAME_REQUIRED);

        return;
      }
      if (!categoryName) {
        setCategoryNameError(CATEGORY_NAME_REQUIRED);
        return;
      }
      setCategoryNameError(undefined);
      addCategory.mutate();
    }, [addCategory, categoryName, rank]);
    const open = dialog.useState('open');
    return (
      <StyledDialog store={dialog} aria-modal="true">
        {open && (
          <Container>
            <Typography variant={TypographyVariant.h2}>
              Create category
            </Typography>
            <Input
              name="Category name*"
              placeholder="Enter a name"
              showLabel
              value={categoryName}
              type="text"
              width="100%"
              onChange={handleChangeCategoryName}
              hasError={Boolean(categoryNameError)}
              errorMessage={categoryNameError}
            />
            <Input
              name="Rank"
              placeholder="Order #"
              showLabel
              value={rank ?? ''}
              type="number"
              width="100%"
              required
              onChange={(event) => {
                setRank(
                  event.target.value ? Number(event.target.value) : undefined
                );
              }}
            />
            <ButtonsContainer>
              <Button
                variant={ButtonVariant.secondary}
                label="Cancel"
                onClick={handleClose}
                width="124px"
              />
              <Button
                variant={ButtonVariant.primary}
                label="Create category"
                onClick={handleCreateCategory}
                width="195px"
              />
            </ButtonsContainer>
          </Container>
        )}
      </StyledDialog>
    );
  }
);

AddCategoryModal.displayName = 'AddCategoryModal';

const StyledDialog = styled(Dialog)`
  ${({ theme }) => css`
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    background: ${theme.palette.black(0.5)};
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${theme.spacing(5)};
  `}
`;

const Container = styled.div`
  ${({ theme }) => css`
    width: 423px;
    height: fit-content;
    padding: ${theme.spacing(5, 5)};
    position: relative;
    background-color: ${theme.palette.neutral['00']};
    border-radius: 10px;
    max-height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `}
`;

const ButtonsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(3)};
    width: 100%;
    margin-top: ${theme.spacing(1)};
    justify-content: center;
  `}
`;

export default memo(AddCategoryModal);
